/* ContactsPage.jsx — Calm operational relationship directory.
 *
 * "People connected to live transactions" — not contact management.
 * Vertically grouped by operational role (Solicitors · Buyers ·
 * Sellers · Brokers · Managing Agents). Each row surfaces:
 *   · LEFT     — person/company + role
 *   · CENTRE   — active transactions with inline stage context
 *   · RIGHT    — operational context (aggregate signals + last active)
 *
 * Doctrine: Resolve > Display · Reduce > Add · Calm > Stimulate.
 * Visual rhythm follows SalesProgressionPage / DealCommandPage.
 */
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, Filter, Plus, ChevronRight, Phone, Mail, ArrowUpRight, MessageCircle,
  Briefcase, User, FileText, Landmark, Building2,
} from "lucide-react";
import { Sidebar } from "../components/Sidebar";
import { useChainStore, openAddContact, DEAL_STAGES } from "../data/chainStore";
import { PageHeader, WORKSPACE_SHELL_CLASS } from "../components/ui/PageHeader";
import { Users } from "lucide-react";
import { whatsappHref } from "../lib/contactLinks";
import { buildRequestDocUrl } from "../lib/documentRequestLaunch";
import { useCanManageContacts } from "../hooks/useDealAccess";
import {
  STAGE_BUCKET, STAGE_TONE,
  composeOperationalContext, TONE_COLOR,
} from "../constants/operationalMomentum";

// ─── Role taxonomy ────────────────────────────────────────────────

const GROUPS = [
  { id: "Solicitors",      single: "Solicitor",      roles: ["Solicitor"],                       dot: "#1D4ED8", Icon: Briefcase },
  { id: "Buyers",          single: "Buyer",          roles: ["Buyer"],                           dot: "#6D28D9", Icon: User       },
  { id: "Sellers",         single: "Seller",         roles: ["Seller"],                          dot: "#047857", Icon: User       },
  { id: "Brokers",         single: "Broker",         roles: ["Mortgage Broker"],                 dot: "#B45309", Icon: Landmark   },
  { id: "Managing Agents", single: "Managing Agent", roles: ["Managing Agent", "Estate Agent"],  dot: "#0E7490", Icon: Building2  },
];

const FILTER_TABS = [
  { id: "all",             label: "All"             },
  { id: "Buyers",          label: "Buyers"          },
  { id: "Sellers",         label: "Sellers"         },
  { id: "Solicitors",      label: "Solicitors"      },
  { id: "Brokers",         label: "Brokers"         },
  { id: "Managing Agents", label: "Managing Agents" },
];

const STAGE_LABEL = Object.fromEntries(DEAL_STAGES.map((s) => [s.id, s.label]));

// ─── Utilities ───────────────────────────────────────────────────

const initials = (s) =>
  String(s || "?").trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();

const groupFor = (role) => GROUPS.find((g) => g.roles.includes(role))?.id || null;

const shortAddress = (a) => {
  if (!a) return null;
  return a.split(",")[0]?.trim() || a;
};

// ─── Page ────────────────────────────────────────────────────────

export default function ContactsPage() {
  const { contacts, findProperty, dealsForContact } = useChainStore();
  const canManage = useCanManageContacts();
  const nav = useNavigate();

  const [search, setSearch]                 = useState("");
  const [tab, setTab]                       = useState("all");
  const [activeThisWeek, setActiveThisWeek] = useState(false);
  const [exchangeReadyOnly, setExchangeReadyOnly] = useState(false);

  // Decorate every contact with live transaction context.
  const decorated = useMemo(() => {
    return contacts.map((c) => {
      const linked = dealsForContact(c.id).filter((d) => !d.archived);
      const transactions = linked.map((d) => {
        const prop = findProperty(d.propertyId);
        return {
          id:         d.id,
          name:       shortAddress(prop?.address) || d.name || "Transaction",
          stage:      d.stage,
          stageLabel: STAGE_BUCKET[d.stage] || STAGE_LABEL[d.stage] || "Active",
          status:     d.status,
          updatedAt:  d.updatedAt,
          side:       (d.buyerSolicitorId === c.id || (d.participants || []).some((pt) => pt.contact_id === c.id && pt.participant_type === "buyer_solicitor")) ? "buyer"
                    : (d.sellerSolicitorId === c.id || (d.participants || []).some((pt) => pt.contact_id === c.id && pt.participant_type === "seller_solicitor")) ? "seller" : null,
        };
      });
      const exchangeReady = transactions.filter((t) => t.stageLabel === "Pre-Exchange" || t.stageLabel === "Exchanged");
      const activeDeals   = transactions.filter((t) => t.status !== "Completed" && t.status !== "Fallen Through");
      return {
        contact:        c,
        group:          groupFor(c.role),
        transactions,
        activeDeals:    activeDeals.length,
        exchangeReady:  exchangeReady.length,
      };
    });
  }, [contacts, dealsForContact, findProperty]);

  // Search filter
  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return decorated;
    return decorated.filter((row) => {
      const c = row.contact;
      return (
        (c.name    || "").toLowerCase().includes(q) ||
        (c.company || "").toLowerCase().includes(q) ||
        (c.role    || "").toLowerCase().includes(q)
      );
    });
  }, [decorated, search]);

  // Optional secondary filters
  const operationalFiltered = useMemo(() => {
    let out = searched;
    if (activeThisWeek)     out = out.filter((r) => r.activeDeals > 0);
    if (exchangeReadyOnly)  out = out.filter((r) => r.exchangeReady > 0);
    return out;
  }, [searched, activeThisWeek, exchangeReadyOnly]);

  // Tab filter
  const tabFiltered = useMemo(() => {
    if (tab === "all") return operationalFiltered;
    return operationalFiltered.filter((r) => r.group === tab);
  }, [operationalFiltered, tab]);

  const groups = useMemo(() => {
    return GROUPS
      .map((g) => ({ meta: g, rows: tabFiltered.filter((r) => r.group === g.id) }))
      .filter((g) => g.rows.length > 0);
  }, [tabFiltered]);

  return (
    <div className="min-h-screen" style={{ background: "#F8F8FB" }}>
      <Sidebar />
      <main className="md:ml-[var(--cf-sidebar-w,300px)] transition-[margin-left] duration-300 ease-out min-h-screen overflow-x-hidden" data-testid="people-main">
        <div className={WORKSPACE_SHELL_CLASS}>
          <Header
            search={search} setSearch={setSearch}
            onAdd={canManage ? () => openAddContact({}) : null}
          />
          <FilterStrip
            tab={tab} setTab={setTab}
            activeThisWeek={activeThisWeek} setActiveThisWeek={setActiveThisWeek}
            exchangeReadyOnly={exchangeReadyOnly} setExchangeReadyOnly={setExchangeReadyOnly}
          />

          <div className="mt-7 space-y-7" data-testid="people-groups">
            {groups.length === 0 ? (
              <EmptyState onAdd={canManage ? () => openAddContact({}) : null} />
            ) : (
              groups.map(({ meta, rows }) => (
                <PersonGroup key={meta.id} meta={meta} rows={rows} nav={nav} />
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────

function Header({ search, setSearch, onAdd }) {
  return (
    <PageHeader
      icon={Users}
      title="Contacts"
      description="Everyone currently involved in live transactions."
      testid="people-header"
      titleTestid="people-title"
      actions={(
        <>
          <div className="relative flex-1 lg:w-[320px]">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search contacts, company or role…"
              className="w-full h-10 pl-8 pr-3 rounded-xl bg-white border border-slate-100 text-[12.5px] text-slate-700 placeholder:text-slate-400 focus:outline-none focus:border-violet-300 focus:ring-2 focus:ring-violet-100 transition-shadow"
              style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}
              data-testid="people-search"
            />
          </div>
          <button
            type="button"
            className="h-10 px-4 rounded-xl bg-white border border-slate-100 text-[12.5px] font-semibold text-slate-700 inline-flex items-center gap-2 hover:bg-slate-50 hover:border-slate-200 transition-colors"
            data-testid="people-filter"
            style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.04)" }}
          >
            <Filter size={13} /> Filter
          </button>
          {onAdd && <button
            type="button"
            onClick={onAdd}
            className="h-10 px-4 rounded-xl text-[12.5px] font-semibold text-white inline-flex items-center gap-2 shrink-0 hover:opacity-95 cf-press transition-opacity"
            style={{
              background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)",
              boxShadow:  "0 8px 20px -10px rgba(15,23,42,0.40)",
            }}
          data-testid="people-add"
        >
          <Plus size={14} strokeWidth={2.5} /> Add Contact
        </button>}
        </>
      )}
    />
  );
}

// ─── Filter strip ────────────────────────────────────────────────

function FilterStrip({ tab, setTab, activeThisWeek, setActiveThisWeek, exchangeReadyOnly, setExchangeReadyOnly }) {
  return (
    <nav
      className="mt-7 flex items-start justify-between gap-3 flex-col lg:flex-row lg:items-center"
      data-testid="people-filterstrip"
    >
      <div className="flex items-center gap-2 flex-wrap overflow-x-auto">
        {FILTER_TABS.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`h-9 px-3.5 rounded-xl text-[12.5px] font-semibold whitespace-nowrap transition-colors ${
                active ? "text-white" : "text-slate-600 border border-slate-100 bg-white hover:bg-slate-50"
              }`}
              style={
                active
                  ? { background: "#0F172A", boxShadow: "0 4px 10px -6px rgba(15,23,42,0.40)" }
                  : undefined
              }
              data-testid={`people-tab-${t.id.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <ToggleChip
          active={activeThisWeek}
          onToggle={() => setActiveThisWeek((v) => !v)}
          testid="people-active-this-week"
          label="Active this week"
        />
        <ToggleChip
          active={exchangeReadyOnly}
          onToggle={() => setExchangeReadyOnly((v) => !v)}
          testid="people-exchange-ready"
          label="Exchange ready deals"
        />
      </div>
    </nav>
  );
}

function ToggleChip({ active, onToggle, testid, label }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      className={`h-9 px-3 rounded-xl text-[11.5px] font-semibold inline-flex items-center gap-2 transition-colors select-none ${
        active
          ? "bg-violet-50 border border-violet-200 text-violet-700"
          : "bg-white border border-slate-100 text-slate-600 hover:bg-slate-50"
      }`}
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
    >
      <span
        aria-hidden
        className={`relative inline-flex w-7 h-4 rounded-full transition-colors ${
          active ? "bg-violet-600" : "bg-slate-200"
        }`}
      >
        <span
          className="absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all"
          style={{ left: active ? "14px" : "2px" }}
        />
      </span>
      {label}
    </button>
  );
}

// ─── Group section ──────────────────────────────────────────────

function PersonGroup({ meta, rows, nav }) {
  const Icon = meta.Icon;
  /* P0 · Contacts "View all" controls operational (2026-08-18)
   * ---------------------------------------------------------------
   * Each `<PersonGroup/>` owns its own local expand/collapse
   * state — no shared cursor, no URL-driven navigation, so
   * expanding "Buyers" never affects "Sellers".  The button
   * toggles between "View all N" and "Show less", persists no
   * server state, and reuses the exact `rows` array the group
   * already receives from the ContactsPage data flow.
   *
   * The control is only rendered when the group has MORE than
   * five rows so a group of five-or-fewer stays visually calm
   * (per the operator brief).
   */
  const [isExpanded, setIsExpanded] = useState(false);
  const canExpand = rows.length > 5;
  const visibleRows = isExpanded ? rows : rows.slice(0, 5);
  const testKey = meta.id.toLowerCase().replace(/\s+/g, "-");
  return (
    <section data-testid={`group-${testKey}`}>
      <header className="flex items-center justify-between mb-3 px-1">
        <div className="flex items-center gap-2">
          <Icon size={14} style={{ color: meta.dot }} />
          <h2 className="text-[14.5px] font-bold tracking-tight text-slate-900">{meta.id}</h2>
          <span className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[10.5px] font-bold text-slate-600 bg-slate-100 tabular-nums">
            {rows.length}
          </span>
        </div>
        {canExpand && (
          <button
            type="button"
            onClick={() => setIsExpanded((prev) => !prev)}
            aria-expanded={isExpanded}
            aria-controls={`group-${testKey}-list`}
            className="text-[11.5px] font-semibold text-slate-500 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/60 focus-visible:ring-offset-1 rounded-md px-1 inline-flex items-center gap-1 transition-colors cursor-pointer"
            data-testid={`group-${testKey}-viewall`}
            data-expanded={isExpanded ? "1" : "0"}
          >
            {isExpanded ? (
              <>Show less <ChevronRight size={11} className="rotate-90" /></>
            ) : (
              <>View all {rows.length} <ChevronRight size={11} /></>
            )}
          </button>
        )}
      </header>

      <div
        className="rounded-2xl bg-white border border-slate-100 overflow-hidden"
        style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
      >
        <ColumnHeader />
        <ul id={`group-${testKey}-list`}>
          {visibleRows.map((row, i) => (
            <PersonRow key={row.contact.id} row={row} index={i} group={meta.id} nav={nav} />
          ))}
        </ul>
      </div>
    </section>
  );
}

function ColumnHeader() {
  return (
    <div className="hidden md:grid grid-cols-[1.3fr_1.7fr_1.4fr_104px] gap-4 px-6 py-2.5 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 border-b border-slate-100 bg-slate-50/40">
      <span>Person</span>
      <span>Active Transactions</span>
      <span>Operational Context</span>
      <span />
    </div>
  );
}

// Most recent real activity across the contact's linked deals; null when there is none.
function lastActiveLabel(transactions) {
  const t = Math.max(...transactions.map((x) => Date.parse(x.updatedAt || "") || 0), 0);
  if (!t) return null;
  const days = Math.floor((Date.now() - t) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days} days ago`;
  return `on ${new Date(t).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
}

function PersonRow({ row, index, group, nav }) {
  const c            = row.contact;
  const groupMeta    = GROUPS.find((g) => g.id === group);
  const lastActive   = lastActiveLabel(row.transactions);
  const contextLines = composeOperationalContext(row);

  // Solicitor side from real linked deal roles (then tags); unknown/both → plain "Solicitor".
  const subRole = (() => {
    if (c.role !== "Solicitor") return c.role;
    const sides = new Set(row.transactions.map((t) => t.side).filter(Boolean));
    if (sides.size === 0) {
      if (c.tags?.includes("seller")) sides.add("seller");
      if (c.tags?.includes("buyer"))  sides.add("buyer");
    }
    if (sides.size !== 1) return "Solicitor";
    return sides.has("buyer") ? "Buyer Solicitor" : "Seller Solicitor";
  })();

  /* iter-alignment · P55 — Whole-row navigation.  Clicking anywhere
   * on the row (avatar · name · empty area · context lines) opens
   * the contact's profile.  Child action buttons + links already
   * call `e.stopPropagation()` so they stay independent.  Keyboard
   * users get Enter / Space to activate + a focus ring; the row
   * is exposed as `role="link"` for assistive tech. */
  const openProfile = () => nav(`/contacts/${c.id}`);
  const onKeyDown = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openProfile();
    }
  };
  return (
    <li
      className="md:grid md:grid-cols-[1.3fr_1.7fr_1.4fr_104px] flex flex-col gap-3 px-5 md:px-6 py-4 md:items-center border-b border-slate-100/70 last:border-b-0 hover:bg-slate-50/60 hover:shadow-[inset_2px_0_0_0_rgba(15,23,42,0.08)] cursor-pointer transition-all duration-200 cf-row-enter focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-inset"
      style={{ animationDelay: `${Math.min(index, 4) * 30}ms` }}
      data-testid={`person-${c.id}`}
      role="link"
      tabIndex={0}
      aria-label={`Open ${c.name}'s profile`}
      onClick={openProfile}
      onKeyDown={onKeyDown}
    >
      {/* Person */}
      <div className="flex items-center gap-3 min-w-0">
        <Avatar name={c.name} tint={groupMeta?.dot} />
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-slate-900 truncate leading-snug">{c.name}</p>
          <p className="text-[11.5px] text-slate-500 truncate">
            {subRole}{c.company ? ` · ${c.company}` : ""}
          </p>
        </div>
      </div>

      {/* Active Transactions — inline stage chips */}
      <div className="min-w-0">
        <p className="md:hidden text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400 mb-1.5">Active Transactions</p>
        {row.transactions.length === 0 ? (
          <span className="text-[12px] text-slate-400">No live transactions</span>
        ) : (
          <ul className="space-y-1">
            {row.transactions.slice(0, 3).map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className="group inline-flex items-center gap-2 text-left max-w-full hover:text-slate-900 transition-colors"
                  onClick={(e) => { e.stopPropagation(); nav(`/deal-rooms/${t.id}`); }}
                  title={`${t.name} · ${t.stageLabel}`}
                  data-testid={`person-${c.id}-deal-${t.id}`}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: STAGE_TONE[t.stageLabel] || "#94A3B8" }}
                  />
                  <span className="text-[12.5px] font-medium text-slate-700 truncate max-w-[180px]">{t.name}</span>
                  <span className="text-slate-300 shrink-0">·</span>
                  <span
                    className="text-[11.5px] font-semibold whitespace-nowrap"
                    style={{ color: STAGE_TONE[t.stageLabel] || "#475569" }}
                  >
                    {t.stageLabel}
                  </span>
                </button>
              </li>
            ))}
            {row.transactions.length > 3 && (
              <li>
                <span className="text-[11.5px] font-semibold text-slate-500">
                  +{row.transactions.length - 3} more
                </span>
              </li>
            )}
          </ul>
        )}
      </div>

      {/* Operational Context */}
      <div className="min-w-0">
        <p className="md:hidden text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400 mb-1.5">Operational Context</p>
        <ul className="space-y-1" data-testid={`person-${c.id}-context`}>
          {contextLines.map((line, i) => (
            <li
              key={i}
              className="text-[12px] leading-snug"
              style={{ color: TONE_COLOR[line.tone] || "#475569", fontWeight: line.tone === "primary" ? 600 : 500 }}
            >
              {line.text}
            </li>
          ))}
          {lastActive && (
            <li className="text-[11.5px] text-slate-400 pt-0.5" data-testid={`person-${c.id}-last-active`}>Last active {lastActive}</li>
          )}
        </ul>
      </div>

      {/* Quick actions */}
      <div className="flex items-center gap-1 justify-start md:justify-end">
        <ActionButton
          Icon={Phone} label={`Call ${c.name}`} testid={`person-call-${c.id}`}
          href={c.phone ? `tel:${c.phone}` : undefined}
        />
        <ActionButton
          Icon={Mail}  label={`Email ${c.name}`} testid={`person-email-${c.id}`}
          href={c.email ? `mailto:${c.email}` : undefined}
        />
        <ActionButton
          Icon={MessageCircle} label={`WhatsApp ${c.name}`} testid={`person-whatsapp-${c.id}`}
          href={whatsappHref(c.phone, `Hi ${c.name.split(" ")[0]} — quick update from ChainFlow.`) || undefined}
        />
        <ActionButton
          Icon={ArrowUpRight}
          label={`Open ${c.name}'s deal room`}
          testid={`person-open-${c.id}`}
          onClick={() => row.transactions[0] && nav(`/deal-rooms/${row.transactions[0].id}`)}
          disabled={row.transactions.length === 0}
        />
        {/* iter-P65 · Contextual Request Document.  Only shows
            when this contact is linked to at least one live deal
            so navigating into the drawer makes sense.  Deep-links
            to the FIRST linked deal — inside the Deal Room the
            recipient picker pre-selects this contact via their
            role.  When a contact spans multiple deals the
            operator picks in the drawer. */}
        <ActionButton
          Icon={FileText}
          label={`Request a document from ${c.name}`}
          testid={`person-request-doc-${c.id}`}
          href={row.transactions[0] ? (buildRequestDocUrl(row.transactions[0].id) || undefined) : undefined}
          disabled={row.transactions.length === 0}
        />
      </div>
    </li>
  );
}

function ActionButton({ Icon, label, testid, onClick, href, disabled }) {
  const base = "w-8 h-8 rounded-lg inline-flex items-center justify-center border border-slate-100 bg-white text-slate-500 hover:text-slate-900 hover:bg-slate-50 hover:border-slate-200 transition-colors";
  const cn   = `${base} ${disabled ? "opacity-40 pointer-events-none" : ""}`;
  if (href && !disabled) {
    return (
      <a
        href={href} className={cn} aria-label={label} data-testid={testid}
        onClick={(e) => e.stopPropagation()}
      >
        <Icon size={12} />
      </a>
    );
  }
  return (
    <button
      type="button" className={cn} aria-label={label} data-testid={testid}
      onClick={(e) => { e.stopPropagation(); onClick?.(); }} disabled={disabled}
    >
      <Icon size={12} />
    </button>
  );
}

function Avatar({ name, tint }) {
  return (
    <span
      className="w-10 h-10 rounded-full inline-flex items-center justify-center text-[11px] font-bold shrink-0"
      style={{
        background: tint ? `${tint}14` : "#F1F5F9",
        color:      tint || "#475569",
        border:     tint ? `1px solid ${tint}28` : "1px solid #E2E8F0",
      }}
    >
      {initials(name)}
    </span>
  );
}

// ─── Empty state ────────────────────────────────────────────────

function EmptyState({ onAdd }) {
  return (
    <section
      className="rounded-2xl bg-white border border-slate-100 p-10 text-center"
      style={{ boxShadow: "0 1px 2px rgba(15,23,42,0.02)" }}
      data-testid="people-empty"
    >
      <span
        className="inline-flex w-12 h-12 rounded-2xl items-center justify-center mb-3"
        style={{ background: "rgba(139,92,246,0.10)", color: "#6D28D9" }}
      >
        <FileText size={18} />
      </span>
      <p className="text-[14px] font-semibold text-slate-900">No contacts match these filters</p>
      <p className="text-[12px] text-slate-500 mt-1">
        Try clearing the search or turn off the operational toggles.
      </p>
      {onAdd && <button
        type="button"
        onClick={onAdd}
        className="mt-5 h-10 px-4 rounded-xl text-[12.5px] font-semibold text-white inline-flex items-center gap-2"
        style={{ background: "linear-gradient(135deg, #0F172A 0%, #1E293B 100%)" }}
        data-testid="people-empty-add"
      >
        <Plus size={13} /> Add Contact
      </button>}
    </section>
  );
}
